import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Skeleton,
  Chip,
  IconButton,
  Card,
  CardContent,
  Fade,
  Tooltip,
  Container,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import InventoryIcon from "@mui/icons-material/Inventory";
import TimelineIcon from "@mui/icons-material/Timeline";
import { GetOrderStatuses } from "../../../services/DashboardService";
import "./Panels.css";

export default function OrdersPanel({ startDate, endDate, selectedUser }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const max = data.length ? Math.max(...data.map((d) => d.count)) : 0;
  const total = data.reduce((sum, item) => sum + item.count, 0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetOrderStatuses(startDate, endDate, selectedUser);
      setData(res || []);
      setLastUpdated(new Date());
    } catch (e) {
      setData([]);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getStatusConfig = (status) => {
    const configs = {
      Delivered: { color: "#007DD1", icon: <CheckCircleOutlineIcon /> },
      "In Transit": { color: "#007DD1", icon: <LocalShippingIcon /> },
      Processing: { color: "#007DD1", icon: <InventoryIcon /> },
      Pending: { color: "#007DD1", icon: <HourglassEmptyIcon /> },
      Cancelled: { color: "#007DD1", icon: <CancelOutlinedIcon /> },
      Returned: { color: "#007DD1", icon: <TimelineIcon /> },
    };
    return configs[status] || {
      color: "#007DD1",
      icon: <InventoryIcon />,
    };
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Grid container spacing={1.5}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header Section */}
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ color: "#007DD1", fontSize: "1.05rem" }}
            >
              Order Status Dashboard
            </Typography>
            <Tooltip title="Real-time order status distribution">
              <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Overview of order distribution by status
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh data">
            <IconButton size="small" onClick={loadData} disabled={loading}>
              <RefreshIcon
                sx={{
                  fontSize: 18,
                  color: "#007DD1",
                  animation: loading ? "spin 1.5s linear infinite" : "none",
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Cards */}
      <Grid container spacing={1.5}>
        {data.map((row, index) => {
          const config = getStatusConfig(row.status);
          const percentage = max ? Math.round((row.count / max) * 100) : 0;
          const sharePercentage = total ? ((row.count / total) * 100).toFixed(1) : 0;

          return (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <Fade in timeout={200 + index * 80}>
                <Card
                  elevation={1}
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                    "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
                  }}
                >
                  <CardContent sx={{ p: "10px !important" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box
                        sx={{
                          bgcolor: `${config.color}15`,
                          color: config.color,
                          p: 0.8,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {config.icon}
                      </Box>
                      <Chip
                        label={`${sharePercentage}%`}
                        size="small"
                        sx={{
                          bgcolor: `${config.color}10`,
                          color: config.color,
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>

                    <Typography
                      variant="subtitle2"
                      sx={{ color: config.color, fontWeight: 600, mb: 0.3 }}
                    >
                      {row.status || "Unknown"}
                    </Typography>

                    <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, lineHeight: 1 }}>
                      {row.count?.toLocaleString() || 0}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      orders in this status
                    </Typography>

                    <Box
                      sx={{
                        mt: 1,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "#E0E0E0",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${percentage}%`,
                          height: "100%",
                          bgcolor: config.color,
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          );
        })}

        {!data?.length && (
          <Grid item xs={12}>
            <Card
              sx={{
                textAlign: "center",
                p: 4,
                borderRadius: 2,
                bgcolor: "#f9f9f9",
              }}
            >
              <InventoryIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
              <Typography variant="subtitle1" color="text.secondary">
                No Order Data Available
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Try changing your filters or date range.
              </Typography>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}
